import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Platform,
  Dimensions,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { CommonActions } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../services/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getColors, typography, spacing, borderRadius, getShadows } from '../styles/theme';
import SubscriptionPaymentModal from '../components/SubscriptionPaymentModal';
import CheckerPattern from '../components/CheckerPattern';
import { getSubscriptionStatus } from '../services/subscriptionService';
import { updateUser } from '../services/database';

const SOCIAL_PROOF_VISIBLE_KEY = 'socialProofClubsVisible';
const SOCIAL_PROOF_CLUBS_KEY = 'socialProofClubs';
const DEFAULT_CLUBS = [
  { name: 'Regent Park Chess Club' },
  { name: 'Hart House (U of T)' },
  { name: 'Vancouver Beer Chess (launch partner)' },
];

const { width: SCREEN_WIDTH } = Dimensions.get('window');
const isMobile = SCREEN_WIDTH < 768;

const PricingScreen = ({ navigation }) => {
  const { isDarkMode } = useTheme();
  const { user, appUser, syncUserWithDatabase } = useAuth();
  const { showToast } = useToast();
  const colors = getColors(isDarkMode);
  const shadows = getShadows(isDarkMode);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [socialProofVisible, setSocialProofVisible] = useState(false);
  const [socialProofClubs, setSocialProofClubs] = useState(DEFAULT_CLUBS);
  const [checkingSubscription, setCheckingSubscription] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        let visibleVal = false;
        let clubsVal = DEFAULT_CLUBS;
        if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
          const v = localStorage.getItem(SOCIAL_PROOF_VISIBLE_KEY);
          visibleVal = v === 'true';
          const c = localStorage.getItem(SOCIAL_PROOF_CLUBS_KEY);
          if (c) {
            try { clubsVal = JSON.parse(c); } catch (_) {}
          }
        } else {
          const v = await AsyncStorage.getItem(SOCIAL_PROOF_VISIBLE_KEY);
          visibleVal = v === 'true';
          const c = await AsyncStorage.getItem(SOCIAL_PROOF_CLUBS_KEY);
          if (c) {
            try { clubsVal = JSON.parse(c); } catch (_) {}
          }
        }
        setSocialProofVisible(visibleVal);
        setSocialProofClubs(Array.isArray(clubsVal) ? clubsVal : DEFAULT_CLUBS);
      } catch (e) {
        console.warn('Failed to load social proof settings', e);
      }
    };
    load();
  }, []);

  // Check if user has active subscription and redirect to Membership screen
  useEffect(() => {
    const checkSubscription = async () => {
      if (!user || !appUser?.id) {
        setCheckingSubscription(false);
        return;
      }

      try {
        // First check local tier - quick check
        const userTier = appUser.tier;
        if (userTier === 'PRO') {
          // User has PRO tier in database, redirect to Membership
          navigation.replace('Membership');
          return;
        }

        // Also check Stripe directly in case tier wasn't updated in database
        // This handles the case where user signed out and back in
        const subscriptionData = await getSubscriptionStatus(appUser.id, appUser.email);
        const hasActiveSubscription = subscriptionData?.activeSubscription && 
          (subscriptionData.activeSubscription.status === 'active' || 
           subscriptionData.activeSubscription.status === 'trialing');
        
        if (hasActiveSubscription) {
          // Update database with subscription info (sync from Stripe)
          // Use 'PRO' instead of 'CLUB' since enum only has FREE and PRO
          try {
            await updateUser(appUser.id, {
              tier: 'PRO',
            });
            // Try to update subscription fields, but don't fail if columns don't exist
            try {
              await updateUser(appUser.id, {
                stripe_subscription_id: subscriptionData.activeSubscription.id,
                stripe_customer_id: subscriptionData.customer?.id,
                subscription_status: subscriptionData.activeSubscription.status,
                subscription_started_at: new Date(subscriptionData.activeSubscription.created * 1000).toISOString(),
                subscription_ends_at: new Date(subscriptionData.activeSubscription.currentPeriodEnd * 1000).toISOString(),
              });
            } catch (subscriptionFieldsError) {
              console.error('Error syncing subscription fields to database (columns may not exist yet):', subscriptionFieldsError);
            }
          } catch (updateError) {
            console.error('Error syncing subscription to database:', updateError);
          }
          
          // Redirect to Membership screen - ALWAYS redirect if Stripe says active, even if DB update fails
          navigation.replace('Membership');
          return;
        } else {
        }
      } catch (error) {
        console.error('Error checking subscription status:', error);
        // Continue showing pricing screen if check fails
      } finally {
        setCheckingSubscription(false);
      }
    };

    checkSubscription();
  }, [user, appUser?.id, appUser?.tier, navigation]);

  const styles = createStyles(colors, shadows, isDarkMode);

  // Show loading while checking subscription
  if (checkingSubscription && user) {
    return (
      <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
        <Text style={[styles.loadingText, { color: colors.textSecondary }]}>
          Loading...
        </Text>
      </View>
    );
  }

  const handleUpgradePress = () => {
    if (!user) {
      showToast('Please login to upgrade to Club Pro', 'info');
      if (navigation.canGoBack()) {
        navigation.navigate('Login');
      }
      return;
    }
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = async (paymentData) => {
    console.log('Subscription successful:', paymentData);
    
    try {
      // Save subscription info to database - update tier first (this column exists)
      // Use 'PRO' instead of 'CLUB' since enum only has FREE and PRO
      await updateUser(appUser.id, {
        tier: 'PRO',
      });
      
      // Try to update subscription fields, but don't fail if columns don't exist
      try {
        await updateUser(appUser.id, {
          stripe_subscription_id: paymentData.subscriptionId,
          stripe_customer_id: paymentData.customerId,
          subscription_status: 'active',
          subscription_started_at: new Date().toISOString(),
        });
      } catch (subscriptionFieldsError) {
        console.error('Error updating subscription fields (columns may not exist yet):', subscriptionFieldsError);
      }
      
      // Refresh user context to get updated tier
      if (syncUserWithDatabase) {
        await syncUserWithDatabase();
      }
      
      showToast('Successfully subscribed to Club Pro!', 'success');
      
      // Navigate to Map screen with welcome flag
      // Use CommonActions to navigate to nested tab screen
      navigation.dispatch(
        CommonActions.navigate({
          name: 'MainTabs',
          params: {
            screen: 'Map',
            params: { showWelcomePro: true },
          },
        })
      );
    } catch (error) {
      console.error('Error saving subscription:', error);
      // Still navigate even if saving fails - subscription is active in Stripe
      showToast('Subscribed! Some data may sync later.', 'success');
      navigation.dispatch(
        CommonActions.navigate({
          name: 'MainTabs',
          params: {
            screen: 'Map',
            params: { showWelcomePro: true },
          },
        })
      );
    }
  };

  const handleStartFree = () => {
    if (!user) {
      navigation.navigate('Register');
    } else {
      navigation.goBack();
    }
  };

  const handleSeeHowItWorks = () => {
    // Scroll to product previews section
    // For now, just show a toast
    showToast('Explore the app to see how it works!', 'info');
  };

  // Problem pain points
  const painPoints = [
    { icon: 'payment', text: 'Chasing people for payments' },
    { icon: 'event-busy', text: 'Managing signups through DMs and spreadsheets' },
    { icon: 'schedule', text: 'Explaining locations and times every week' },
    { icon: 'calculate', text: 'Manually creating pairings and brackets' },
    { icon: 'people-outline', text: 'Great events with lower turnout than expected' },
  ];

  // Before/After comparison
  const beforeItems = [
    { icon: 'description', text: 'Google Forms' },
    { icon: 'account-balance-wallet', text: 'E-transfers' },
    { icon: 'table-chart', text: 'Spreadsheets' },
    { icon: 'comment', text: 'Instagram comments' },
    { icon: 'notifications-off', text: 'Manual reminders' },
  ];

  const afterItems = [
    { icon: 'map', text: 'Events visible on the map' },
    { icon: 'how-to-reg', text: 'One-click signups' },
    { icon: 'payments', text: 'Built-in payments' },
    { icon: 'people', text: 'Live player lists' },
    { icon: 'emoji-events', text: 'Tournament tools' },
    { icon: 'notifications-active', text: 'Automatic reminders' },
  ];

  // Feature highlights (outcome-focused)
  const featureHighlights = [
    {
      icon: 'map',
      emoji: '📍',
      title: 'Be discovered on the map',
      description: 'Players find your club where they already are.',
    },
    {
      icon: 'people',
      emoji: '👥',
      title: 'Simple event signups',
      description: 'See who\'s coming, instantly.',
    },
    {
      icon: 'payments',
      emoji: '💳',
      title: 'Built-in payments',
      description: 'No more chasing transfers.',
    },
    {
      icon: 'emoji-events',
      emoji: '🏆',
      title: 'Tournament tools',
      description: 'Swiss, round-robin, and pairings made easy.',
    },
    {
      icon: 'notifications',
      emoji: '🔔',
      title: 'Announcements & updates',
      description: 'Reach your players directly.',
    },
    {
      icon: 'bar-chart',
      emoji: '📊',
      title: 'Club insights',
      description: 'See what\'s growing and what\'s working.',
    },
  ];

  // Product previews
  const productPreviews = [
    { icon: 'event', title: 'Create an event in under a minute' },
    { icon: 'map', title: 'Players discover you on the map' },
    { icon: 'people', title: 'Track attendance and payments' },
    { icon: 'emoji-events', title: 'Manage tournaments without spreadsheets' },
  ];

  return (
    <View style={styles.container}>
      {/* Simple Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.navigate('Map')} style={styles.backButton}>
          <Icon name="arrow-back" size={24} color={colors.text} />
          <Text style={styles.backText}>Back</Text>
        </TouchableOpacity>
      </View>

      <ScrollView 
        style={styles.content} 
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        <View style={{ flexGrow: 1, position: 'relative' }}>
          <CheckerPattern isDarkMode={isDarkMode} opacity={0.008} height={6000} />
          <View style={{ zIndex: 1 }}>
        {/* 1. Hero Section */}
        <View style={styles.heroSection}>
          <Text style={styles.heroHeadline}>Need help running your chess club?</Text>
          <Text style={styles.heroSubtext}>
            ChessMaps helps you manage events, players, and payments — so you can focus on building the community and playing chess.
          </Text>
          <View style={[styles.heroCTAs, user && { justifyContent: 'center', alignItems: 'center' }]}>
            {!user && (
              <TouchableOpacity 
                style={[styles.primaryCTA, { backgroundColor: colors.primary }]}
                onPress={handleStartFree}
              >
                <Text style={styles.primaryCTAText}>Start free</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity 
              style={[styles.secondaryCTA, { borderColor: colors.border }]}
              onPress={handleSeeHowItWorks}
            >
              <Text style={[styles.secondaryCTAText, { color: colors.text }]}>See how it works</Text>
            </TouchableOpacity>
          </View>
          <Text style={styles.heroNote}>
            Free to use for clubs • No contracts • Upgrade only if you need it
          </Text>
        </View>

        {/* 2. Problem Empathy Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Running a chess club shouldn't feel like extra work.</Text>
          <Text style={styles.empathyBody}>
            Most clubs aren't short on passion — they're short on time.
          </Text>
          <View style={styles.painPointsList}>
            {painPoints.map((point, index) => (
              <View key={index} style={styles.painPointItem}>
                <Icon name={point.icon} size={20} color={colors.textSecondary} />
                <Text style={[styles.painPointText, { color: colors.text }]}>{point.text}</Text>
              </View>
            ))}
          </View>
          <Text style={styles.empathyNote}>
            ChessMaps was built with real clubs to solve these exact problems.
          </Text>
        </View>

        {/* 3. Before/After Value Proposition */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>From scattered tools to one place.</Text>
          <View style={styles.beforeAfterContainer}>
            <View style={[styles.beforeAfterCard, { backgroundColor: isDarkMode ? colors.backgroundDark : colors.background }]}>
              <Text style={[styles.beforeAfterCardTitle, { color: colors.textSecondary }]}>Before ChessMaps</Text>
              <View style={styles.beforeAfterItemsList}>
                {beforeItems.map((item, index) => (
                  <View key={index} style={styles.beforeAfterItem}>
                    <Icon name={item.icon} size={20} color={colors.textLight} />
                    <Text style={[styles.beforeAfterItemText, { color: colors.textSecondary }]}>{item.text}</Text>
                  </View>
                ))}
              </View>
            </View>
            <View style={[styles.beforeAfterCard, { backgroundColor: colors.primary + '15' }]}>
              <Text style={[styles.beforeAfterCardTitle, { color: colors.text }]}>With ChessMaps</Text>
              <View style={styles.beforeAfterItemsList}>
                {afterItems.map((item, index) => (
                  <View key={index} style={styles.beforeAfterItem}>
                    <Icon name={item.icon} size={20} color={colors.primary} />
                    <Text style={[styles.beforeAfterItemText, { color: colors.text }]}>{item.text}</Text>
                  </View>
                ))}
              </View>
            </View>
          </View>
          <Text style={styles.beforeAfterCaption}>Less admin. More chess.</Text>
        </View>

        {/* 4. Monetization Empowerment */}
        <View style={[styles.section, styles.monetizationSection, { backgroundColor: colors.surface }]}>
          <Text style={styles.sectionTitle}>Clubs stay in control — always.</Text>
          <Text style={styles.monetizationText}>
            ChessMaps doesn't force you to charge for events.{'\n'}
            Free chess stays free.
          </Text>
          <Text style={styles.monetizationText}>
            If you do want to run paid events, tournaments, or memberships, ChessMaps gives you the tools to do it cleanly and transparently.
          </Text>
          <View style={styles.monetizationPoints}>
            <View style={styles.monetizationPoint}>
              <Icon name="check-circle" size={24} color={colors.primary} />
              <Text style={[styles.monetizationPointText, { color: colors.text }]}>Run free or paid events — your choice</Text>
            </View>
            <View style={styles.monetizationPoint}>
              <Icon name="check-circle" size={24} color={colors.primary} />
              <Text style={[styles.monetizationPointText, { color: colors.text }]}>Set your own prices</Text>
            </View>
            <View style={styles.monetizationPoint}>
              <Icon name="check-circle" size={24} color={colors.primary} />
              <Text style={[styles.monetizationPointText, { color: colors.text }]}>Get paid directly through the app</Text>
            </View>
            <View style={styles.monetizationPoint}>
              <Icon name="check-circle" size={24} color={colors.primary} />
              <Text style={[styles.monetizationPointText, { color: colors.text }]}>ChessMaps takes a small fee only when you earn</Text>
            </View>
          </View>
          <Text style={styles.monetizationReassurance}>
            We only succeed when your club grows.
          </Text>
        </View>

        {/* 5. Feature Highlights (Outcome-focused) */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Everything clubs need — nothing they don't.</Text>
          <View style={styles.featuresGrid}>
            {featureHighlights.map((feature, index) => (
              <View key={index} style={[styles.featureCard, { backgroundColor: colors.surface }]}>
                <Text style={styles.featureEmoji}>{feature.emoji}</Text>
                <Text style={[styles.featureTitle, { color: colors.text }]}>{feature.title}</Text>
                <Text style={[styles.featureDescription, { color: colors.textSecondary }]}>
                  {feature.description}
                </Text>
              </View>
            ))}
          </View>
        </View>

        {/* 6. Product Preview Section */}
        <View style={[styles.section, { alignItems: 'center' }]}>
          <Text style={styles.sectionTitle}>What clubs see inside ChessMaps</Text>
          <ScrollView 
            horizontal 
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.previewsContainer}
          >
            {productPreviews.map((preview, index) => (
              <View key={index} style={[styles.previewCard, { backgroundColor: colors.surface }]}>
                <View style={[styles.previewIconContainer, { backgroundColor: colors.backgroundDark }]}>
                  <Icon name={preview.icon} size={48} color={colors.primary} />
                </View>
                <Text style={[styles.previewDescription, { color: colors.textSecondary }]}>
                  {preview.title}
                </Text>
              </View>
            ))}
          </ScrollView>
        </View>

        {/* 7. Pricing Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Start free. Upgrade when it makes sense.</Text>
          <View style={styles.pricingCardsContainer}>
            {/* Free Tier */}
            <View style={[styles.pricingCard, styles.freePricingCard, { backgroundColor: colors.surface }]}>
              <Text style={[styles.pricingTierName, { color: colors.text }]}>Free</Text>
              <Text style={[styles.pricingTierSubtitle, { color: colors.textSecondary }]}>For clubs getting started</Text>
              <View style={styles.pricingFeaturesList}>
                <View style={styles.pricingFeature}>
                  <Icon name="check-circle" size={20} color={colors.primary} />
                  <Text style={[styles.pricingFeatureText, { color: colors.text }]}>Club profile on the map</Text>
                </View>
                <View style={styles.pricingFeature}>
                  <Icon name="check-circle" size={20} color={colors.primary} />
                  <Text style={[styles.pricingFeatureText, { color: colors.text }]}>Free events</Text>
                </View>
                <View style={styles.pricingFeature}>
                  <Icon name="check-circle" size={20} color={colors.primary} />
                  <Text style={[styles.pricingFeatureText, { color: colors.text }]}>Player discovery</Text>
                </View>
                <View style={styles.pricingFeature}>
                  <Icon name="check-circle" size={20} color={colors.primary} />
                  <Text style={[styles.pricingFeatureText, { color: colors.text }]}>Basic event management</Text>
                </View>
              </View>
              <Text style={[styles.pricingPrice, { color: colors.primary }]}>Free</Text>
              {user ? (
                <Text style={[styles.pricingButtonText, { color: colors.textSecondary, marginTop: 12 }]}>Current plan</Text>
              ) : (
                <TouchableOpacity 
                  style={[styles.pricingButton, styles.freePricingButton, { borderColor: colors.border }]}
                  onPress={handleStartFree}
                >
                  <Text style={[styles.pricingButtonText, { color: colors.text }]}>Start free</Text>
                </TouchableOpacity>
              )}
            </View>

            {/* Club Pro Tier */}
            <View style={[styles.pricingCard, styles.proPricingCard, { backgroundColor: colors.surface, borderColor: colors.primary }]}>
              <Text style={[styles.pricingTierName, { color: colors.text }]}>Club Pro</Text>
              <Text style={[styles.pricingTierSubtitle, { color: colors.textSecondary }]}>For clubs running regular events or tournaments</Text>
              <View style={styles.pricingFeaturesList}>
                <View style={styles.pricingFeature}>
                  <Icon name="check-circle" size={20} color={colors.primary} />
                  <Text style={[styles.pricingFeatureText, { color: colors.text }]}>Everything in Free</Text>
                </View>
                <View style={styles.pricingFeature}>
                  <Icon name="check-circle" size={20} color={colors.primary} />
                  <Text style={[styles.pricingFeatureText, { color: colors.text }]}>Paid events & payments</Text>
                </View>
                <View style={styles.pricingFeature}>
                  <Icon name="check-circle" size={20} color={colors.primary} />
                  <Text style={[styles.pricingFeatureText, { color: colors.text }]}>Tournament management tools</Text>
                </View>
                <View style={styles.pricingFeature}>
                  <Icon name="check-circle" size={20} color={colors.primary} />
                  <Text style={[styles.pricingFeatureText, { color: colors.text }]}>Attendance tracking</Text>
                </View>
                <View style={styles.pricingFeature}>
                  <Icon name="check-circle" size={20} color={colors.primary} />
                  <Text style={[styles.pricingFeatureText, { color: colors.text }]}>Priority support</Text>
                </View>
              </View>
              <Text style={[styles.pricingPrice, { color: colors.primary }]}>$30</Text>
              <Text style={[styles.pricingPeriod, { color: colors.textSecondary }]}>per month</Text>
              <TouchableOpacity 
                style={[styles.pricingButton, styles.proPricingButton, { backgroundColor: colors.primary }]}
                onPress={handleUpgradePress}
              >
                <Text style={styles.proPricingButtonText}>Upgrade when ready</Text>
              </TouchableOpacity>
            </View>
          </View>
          <Text style={styles.pricingNote}>No contracts • Cancel anytime</Text>
        </View>

        {/* 8. Trust / Community-First Section */}
        <View style={[styles.section, styles.trustSection, { backgroundColor: colors.surface }]}>
          <Text style={styles.sectionTitle}>Built for the chess community.</Text>
          <Text style={styles.trustBody}>
            ChessMaps was created to grow in-person chess — not to gatekeep it.
          </Text>
          <View style={styles.trustPoints}>
            <View style={styles.trustPoint}>
              <Icon name="check-circle" size={24} color={colors.primary} />
              <Text style={[styles.trustPointText, { color: colors.text }]}>Free chess stays free</Text>
            </View>
            <View style={styles.trustPoint}>
              <Icon name="check-circle" size={24} color={colors.primary} />
              <Text style={[styles.trustPointText, { color: colors.text }]}>Clubs control pricing and events</Text>
            </View>
            <View style={styles.trustPoint}>
              <Icon name="check-circle" size={24} color={colors.primary} />
              <Text style={[styles.trustPointText, { color: colors.text }]}>No long-term commitments</Text>
            </View>
            <View style={styles.trustPoint}>
              <Icon name="check-circle" size={24} color={colors.primary} />
              <Text style={[styles.trustPointText, { color: colors.text }]}>Community comes first</Text>
            </View>
          </View>
        </View>

        {/* 9. Optional Social Proof - visibility controlled by admin in Profile */}
        {socialProofVisible && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Clubs already using ChessMaps</Text>
          <View style={styles.socialProofList}>
            {socialProofClubs.map((club, index) => (
              <View key={index} style={[styles.socialProofItem, { backgroundColor: colors.surface }]}>
                <Icon name="business" size={24} color={colors.primary} />
                <Text style={[styles.socialProofText, { color: colors.text }]}>{typeof club === 'string' ? club : club?.name || ''}</Text>
              </View>
            ))}
          </View>
          <Text style={styles.socialProofNote}>
            Launch partners get early access and free setup.
          </Text>
        </View>
        )}

        {/* Bottom Spacing */}
        <View style={{ height: 60 }} />
          </View>
        </View>
      </ScrollView>

      {/* Subscription Payment Modal - only mount when opening to avoid overlay showing on load (web) */}
      {user && showPaymentModal && (
        <SubscriptionPaymentModal
          visible={true}
          onClose={() => setShowPaymentModal(false)}
          onSuccess={handlePaymentSuccess}
          userId={appUser?.id || user?.id}
          email={appUser?.email || user?.emailAddresses?.[0]?.emailAddress || user?.primaryEmailAddress?.emailAddress}
          clubName={appUser?.clubName || appUser?.username || user?.firstName || 'Your Club'}
          amount={30}
        />
      )}
    </View>
  );
};

const createStyles = (colors, shadows, isDarkMode) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
    paddingTop: Platform.OS === 'ios' ? 50 : 20,
  },
  header: {
    padding: spacing.lg,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  backText: {
    fontSize: typography.body,
    color: colors.text,
    marginLeft: spacing.xs,
  },
  content: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: spacing.xxl,
  },
  // Hero Section
  heroSection: {
    padding: spacing.xl,
    paddingTop: spacing.xxl,
    alignItems: 'center',
    maxWidth: 700,
    alignSelf: 'center',
    width: '100%',
  },
  heroHeadline: {
    fontSize: isMobile ? typography.h1 : 40,
    fontWeight: typography.bold,
    color: colors.text,
    textAlign: 'center',
    marginBottom: spacing.md,
    lineHeight: isMobile ? 40 : 48,
  },
  heroSubtext: {
    fontSize: typography.h5,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: 26,
    marginBottom: spacing.xl,
  },
  heroCTAs: {
    flexDirection: isMobile ? 'column' : 'row',
    gap: spacing.md,
    width: '100%',
    maxWidth: 400,
    marginBottom: spacing.md,
  },
  heroNote: {
    fontSize: typography.caption,
    color: colors.textSecondary,
    textAlign: 'center',
    marginTop: spacing.md,
  },
  primaryCTA: {
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.xl,
    borderRadius: borderRadius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    ...shadows.medium,
  },
  primaryCTAText: {
    color: '#FFF',
    fontSize: typography.h6,
    fontWeight: typography.semiBold,
  },
  secondaryCTA: {
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.xl,
    borderRadius: borderRadius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
  },
  secondaryCTAText: {
    fontSize: typography.h6,
    fontWeight: typography.semiBold,
  },
  // Section Styles
  section: {
    padding: spacing.xl,
    maxWidth: 1200,
    alignSelf: 'center',
    width: '100%',
  },
  sectionTitle: {
    fontSize: typography.h2,
    fontWeight: typography.bold,
    color: colors.text,
    textAlign: 'center',
    marginBottom: spacing.xl,
  },
  // Empathy Section
  empathyBody: {
    fontSize: typography.h5,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: 26,
    marginBottom: spacing.lg,
    maxWidth: 600,
    alignSelf: 'center',
  },
  painPointsList: {
    gap: spacing.md,
    maxWidth: 600,
    alignSelf: 'center',
    width: '100%',
    marginBottom: spacing.lg,
  },
  painPointItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  painPointText: {
    fontSize: typography.body,
    lineHeight: 24,
    flex: 1,
  },
  empathyNote: {
    fontSize: typography.body,
    color: colors.textSecondary,
    textAlign: 'center',
    fontStyle: 'italic',
    maxWidth: 600,
    alignSelf: 'center',
  },
  // Before/After
  beforeAfterContainer: {
    flexDirection: isMobile ? 'column' : 'row',
    gap: spacing.lg,
    marginBottom: spacing.md,
  },
  beforeAfterCard: {
    flex: 1,
    padding: spacing.lg,
    borderRadius: borderRadius.lg,
    minWidth: isMobile ? '100%' : 300,
  },
  beforeAfterCardTitle: {
    fontSize: typography.h6,
    fontWeight: typography.semiBold,
    marginBottom: spacing.md,
    textAlign: 'center',
  },
  beforeAfterItemsList: {
    gap: spacing.sm,
  },
  beforeAfterItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  beforeAfterItemText: {
    fontSize: typography.body,
    lineHeight: 22,
    flex: 1,
  },
  beforeAfterCaption: {
    fontSize: typography.body,
    color: colors.textSecondary,
    textAlign: 'center',
    fontStyle: 'italic',
    marginTop: spacing.md,
  },
  // Monetization
  monetizationSection: {
    alignItems: 'center',
    padding: spacing.xxl,
    borderRadius: borderRadius.xl,
    marginHorizontal: spacing.lg,
    marginVertical: spacing.lg,
    ...shadows.medium,
  },
  monetizationText: {
    fontSize: typography.h5,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: 26,
    marginBottom: spacing.lg,
    maxWidth: 600,
  },
  monetizationPoints: {
    gap: spacing.md,
    maxWidth: 600,
    width: '100%',
    marginBottom: spacing.lg,
  },
  monetizationPoint: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
  },
  monetizationPointText: {
    fontSize: typography.body,
    lineHeight: 24,
    flex: 1,
  },
  monetizationReassurance: {
    fontSize: typography.body,
    color: colors.textSecondary,
    textAlign: 'center',
    fontStyle: 'italic',
    maxWidth: 600,
  },
  // Feature Highlights
  featuresGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
    justifyContent: 'center',
  },
  featureCard: {
    flex: 1,
    minWidth: isMobile ? '100%' : 280,
    maxWidth: 350,
    padding: spacing.lg,
    borderRadius: borderRadius.lg,
    alignItems: 'center',
    gap: spacing.sm,
    ...shadows.small,
  },
  featureEmoji: {
    fontSize: 40,
    marginBottom: spacing.xs,
  },
  featureTitle: {
    fontSize: typography.h6,
    fontWeight: typography.semiBold,
    textAlign: 'center',
    marginBottom: spacing.xs,
  },
  featureDescription: {
    fontSize: typography.body,
    textAlign: 'center',
    lineHeight: 22,
  },
  // Product Previews
  previewsContainer: {
    paddingHorizontal: spacing.md,
    gap: spacing.md,
    justifyContent: 'center',
    alignItems: 'center',
  },
  previewCard: {
    width: 200,
    padding: spacing.lg,
    borderRadius: borderRadius.lg,
    alignItems: 'center',
    gap: spacing.md,
    ...shadows.small,
  },
  previewIconContainer: {
    width: 100,
    height: 100,
    borderRadius: borderRadius.lg,
    alignItems: 'center',
    justifyContent: 'center',
  },
  previewDescription: {
    fontSize: typography.body,
    textAlign: 'center',
    lineHeight: 22,
  },
  // Pricing
  pricingCardsContainer: {
    flexDirection: isMobile ? 'column' : 'row',
    gap: spacing.lg,
    justifyContent: 'center',
  },
  pricingCard: {
    flex: 1,
    maxWidth: isMobile ? '100%' : 400,
    padding: spacing.xl,
    borderRadius: borderRadius.xl,
    borderWidth: 2,
    alignItems: 'center',
    position: 'relative',
    ...shadows.medium,
  },
  freePricingCard: {
    borderColor: colors.border,
  },
  proPricingCard: {
    borderColor: colors.primary,
  },
  proBadge: {
    position: 'absolute',
    top: -12,
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: borderRadius.pill,
  },
  proBadgeText: {
    color: '#FFF',
    fontSize: typography.caption,
    fontWeight: typography.bold,
    letterSpacing: 0.5,
  },
  pricingTierName: {
    fontSize: typography.h3,
    fontWeight: typography.bold,
    marginTop: spacing.md,
    marginBottom: spacing.xs,
    textAlign: 'center',
  },
  pricingTierSubtitle: {
    fontSize: typography.body,
    textAlign: 'center',
    marginBottom: spacing.lg,
  },
  pricingFeaturesList: {
    gap: spacing.sm,
    marginBottom: spacing.lg,
    width: '100%',
  },
  pricingFeature: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  pricingFeatureText: {
    fontSize: typography.body,
    lineHeight: 22,
    flex: 1,
  },
  pricingPrice: {
    fontSize: 48,
    fontWeight: typography.bold,
    lineHeight: 56,
    textAlign: 'center',
    marginTop: spacing.md,
  },
  pricingPeriod: {
    fontSize: typography.body,
    textAlign: 'center',
    marginBottom: spacing.lg,
  },
  pricingNote: {
    fontSize: typography.body,
    color: colors.textSecondary,
    textAlign: 'center',
    marginTop: spacing.lg,
  },
  pricingButton: {
    width: '100%',
    paddingVertical: spacing.md,
    borderRadius: borderRadius.lg,
    alignItems: 'center',
    justifyContent: 'center',
  },
  freePricingButton: {
    borderWidth: 2,
    backgroundColor: 'transparent',
  },
  proPricingButton: {
    ...shadows.small,
  },
  pricingButtonText: {
    fontSize: typography.h6,
    fontWeight: typography.semiBold,
  },
  proPricingButtonText: {
    fontSize: typography.h6,
    fontWeight: typography.semiBold,
    color: '#FFF',
  },
  // Trust Section
  trustSection: {
    alignItems: 'center',
    padding: spacing.xxl,
    borderRadius: borderRadius.xl,
    marginHorizontal: spacing.lg,
    marginTop: spacing.lg,
    ...shadows.medium,
  },
  trustBody: {
    fontSize: typography.h5,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: 26,
    marginBottom: spacing.xl,
    maxWidth: 600,
  },
  trustPoints: {
    gap: spacing.md,
    maxWidth: 600,
    width: '100%',
  },
  trustPoint: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  trustPointText: {
    fontSize: typography.body,
    lineHeight: 24,
    flex: 1,
  },
  // Social Proof
  socialProofList: {
    gap: spacing.md,
    maxWidth: 600,
    alignSelf: 'center',
    width: '100%',
    marginBottom: spacing.md,
  },
  socialProofItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: borderRadius.lg,
    ...shadows.small,
  },
  socialProofText: {
    fontSize: typography.body,
    fontWeight: typography.medium,
    flex: 1,
  },
  socialProofNote: {
    fontSize: typography.body,
    color: colors.textSecondary,
    textAlign: 'center',
    fontStyle: 'italic',
    maxWidth: 600,
    alignSelf: 'center',
  },
  loadingText: {
    fontSize: typography.body,
    marginTop: spacing.md,
  },
});

export default PricingScreen;
